/**
 * TB-303 Bass Line Synthesizer
 * Émulation du synthétiseur Roland TB-303
 *
 * Caractéristiques du son acid :
 * - Filtre résonant 24dB avec envelope
 * - Accent : augmente volume + cutoff + decay court
 * - Slide : portamento entre les notes
 *
 * Améliorations v2.3 :
 * - Saturation pré-filtre (drive d'entrée)
 * - Saturation dans le feedback du filtre (son acid authentique)
 * - Self-oscillation à haute résonance
 * - Compensation de volume à haute résonance
 */

import * as Tone from 'tone';

export class TB303 {
    constructor(audioEngine, id = 1, options = {}) {
        this.audioEngine = audioEngine;
        this.performance = options.performance || { lowPower: false };
        this.id = id;  // Pour identifier TB303 #1 ou #2

        // État power (pour économiser les ressources)
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this.params = {
            cutoff: 1000,
            resonance: 15,
            envMod: 2000,
            decay: 0.2,
            accentLevel: 0.5,  // Niveau global d'accent
            waveform: 'sawtooth',
            slideTime: 0.06,   // Temps de glide en secondes
            distortion: 0.2,   // Saturation post-filtre (0-1)
            drive: 0.3         // Saturation pré-filtre (0-1) - nouveau!
        };

        // État pour le slide
        this.lastNote = null;
        this.slideActive = false;

        this.setupAudio();
    }

    setupAudio() {
        // Connecter au canal du mixer (le mixer gère les sends)
        const mixerChannelId = `tb303_${this.id}`;
        this.mixerChannel = this.audioEngine.getMixerChannelInput(mixerChannelId);

        // === CHAÎNE AUDIO AMÉLIORÉE ===
        // Synth -> Drive (pré-filtre) -> Filtre -> Feedback Saturation -> Distortion -> Output

        // 1. Drive pré-filtre (simule l'étage d'entrée saturé de la 303)
        // Utilise un waveshaper avec courbe tanh douce
        this.inputDrive = new Tone.WaveShaper((x) => {
            const drive = this.params.drive * 3 + 1; // 1 à 4
            return Math.tanh(x * drive) / Math.tanh(drive);
        }, 1024);

        // 2. Filtre résonant 24dB (caractéristique du son 303)
        // Q augmenté pour permettre self-oscillation
        this.filter = new Tone.Filter({
            frequency: this.params.cutoff,
            type: 'lowpass',
            rolloff: -24,
            Q: this._calculateQ(this.params.resonance)
        });

        // 3. Saturation dans le feedback du filtre (le secret du son acid!)
        // Simule la saturation des transistors dans le circuit de résonance
        this.feedbackSaturation = new Tone.WaveShaper((x) => {
            // Soft clipping asymétrique (comme les transistors)
            const k = 0.7;
            if (x > 0) {
                return 1 - Math.exp(-x * k);
            } else {
                return -1 + Math.exp(x * k);
            }
        }, 2048);

        // 4. Gain pour compenser la perte de volume à haute résonance
        this.resonanceCompensation = new Tone.Gain(1);

        // 5. Distortion post-filtre (grain analogique)
        this.distortion = new Tone.Distortion({
            distortion: this.params.distortion,
            oversample: '2x'
        });

        // === CONNEXIONS ===
        // Drive -> Filtre -> Feedback Sat -> Compensation -> Distortion -> Output
        this.inputDrive.connect(this.filter);
        this.filter.connect(this.feedbackSaturation);
        this.feedbackSaturation.connect(this.resonanceCompensation);
        this.resonanceCompensation.connect(this.distortion);
        this.distortion.connect(this.mixerChannel);

        // Envelope pour le filtre (donne le "squelch" acide)
        this.filterEnv = new Tone.FrequencyEnvelope({
            attack: 0.001,
            decay: this.params.decay,
            sustain: 0.1,
            release: 0.1,
            baseFrequency: this.params.cutoff,
            octaves: this.params.envMod / 1000
        }).connect(this.filter.frequency);

        // Synth mono pour la basse (avec portamento pour le slide)
        this.synth = new Tone.MonoSynth({
            oscillator: { type: this.params.waveform },
            envelope: {
                attack: 0.001,
                decay: this.params.decay,
                sustain: 0.4,
                release: 0.1
            },
            filterEnvelope: {
                attack: 0.001,
                decay: this.params.decay,
                sustain: 0.2,
                release: 0.1,
                baseFrequency: 200,
                octaves: 3
            }
        }).connect(this.inputDrive);  // Connecté au drive pré-filtre

        // Le portamento est désactivé par défaut, activé seulement pour le slide
        this.synth.portamento = 0;

        // Appliquer la compensation initiale
        this._updateResonanceCompensation();
    }

    /**
     * Calcule le Q du filtre avec une courbe exponentielle
     * pour permettre la self-oscillation aux valeurs élevées
     */
    _calculateQ(resonance) {
        // Courbe exponentielle : 0-30 -> Q de 1 à 25
        // Les valeurs > 20 permettent la self-oscillation
        const minQ = 1;
        const maxQ = 25;
        const normalized = resonance / 30;
        return minQ + (maxQ - minQ) * Math.pow(normalized, 1.5);
    }

    /**
     * Compense la perte de volume quand la résonance augmente
     */
    _updateResonanceCompensation() {
        // Plus la résonance est haute, plus on compense
        const compensation = 1 + (this.params.resonance / 30) * 0.5;
        this.resonanceCompensation.gain.value = compensation;
    }

    /**
     * Joue une note avec support accent et slide
     * @param {string} note - Note à jouer (ex: 'C2')
     * @param {object} options - Options: { accent, slide, duration, time }
     */
    triggerNote(note, options = {}) {
        const {
            accent = false,
            slide = false,
            duration = '16n',
            time = Tone.now()
        } = options;

        // Configurer le portamento selon si on slide depuis la note précédente
        if (this.slideActive) {
            // La note précédente avait slide ON, donc on glisse vers cette note
            this.synth.portamento = this.params.slideTime;
        } else {
            this.synth.portamento = 0;
        }

        // Mémoriser si cette note a slide pour la prochaine
        this.slideActive = slide;

        // Appliquer l'accent si activé
        if (accent) {
            // Accent : volume +, filtre ouvert +, decay court
            const accentBoost = this.params.accentLevel * 6;  // +6dB max
            const cutoffBoost = this.params.cutoff * (1 + this.params.accentLevel);

            // Boost temporaire du filtre pour cette note
            this.filterEnv.baseFrequency = cutoffBoost;
            this.filterEnv.octaves = (this.params.envMod / 1000) * (1 + this.params.accentLevel * 0.5);
            this.filterEnv.decay = this.params.decay * 0.6;  // Decay plus court

            // Volume boost
            this.synth.volume.value = -10 + accentBoost;
        } else {
            // Valeurs normales
            this.filterEnv.baseFrequency = this.params.cutoff;
            this.filterEnv.octaves = this.params.envMod / 1000;
            this.filterEnv.decay = this.params.decay;
            this.synth.volume.value = -10;
        }

        // Déclencher les envelopes et la note
        this.filterEnv.triggerAttackRelease(0.1, time);
        this.synth.triggerAttackRelease(note, duration, time);

        this.lastNote = note;
    }

    /**
     * Reset du slide à la fin d'un pattern ou stop
     */
    resetSlide() {
        this.slideActive = false;
        this.lastNote = null;
        this.synth.portamento = 0;
    }

    // --- Setters pour les paramètres ---

    setCutoff(value) {
        this.params.cutoff = value;
        this.filter.frequency.value = value;
        // Le cutoff définit aussi la base de l'envelope filtre
        // Sinon l'envelope ignore le cutoff quand elle est active
        this.filterEnv.baseFrequency = value;
    }

    setResonance(value) {
        this.params.resonance = value;
        this.filter.Q.value = this._calculateQ(value);
        this._updateResonanceCompensation();
    }

    setEnvMod(value) {
        this.params.envMod = value;
        this.filterEnv.octaves = value / 1000;
    }

    setDecay(value) {
        this.params.decay = value;
        this.filterEnv.decay = value;
        this.synth.envelope.decay = value;
    }

    setAccentLevel(value) {
        this.params.accentLevel = value;
    }

    setSlideTime(value) {
        this.params.slideTime = value;
    }

    setWaveform(type) {
        this.params.waveform = type;
        this.synth.oscillator.type = type;
    }

    setDistortion(value) {
        this.params.distortion = value;
        this.distortion.distortion = value;
    }

    setDrive(value) {
        this.params.drive = value;
        // Recréer la courbe du waveshaper avec le nouveau drive
        this.inputDrive.curve = this._createDriveCurve(value);
    }

    /**
     * Crée la courbe de saturation pour le drive pré-filtre
     */
    _createDriveCurve(driveAmount) {
        const samples = 1024;
        const curve = new Float32Array(samples);
        const drive = driveAmount * 3 + 1; // 1 à 4

        for (let i = 0; i < samples; i++) {
            const x = (i / (samples - 1)) * 2 - 1; // -1 à 1
            curve[i] = Math.tanh(x * drive) / Math.tanh(drive);
        }
        return curve;
    }

    // --- Getters pour les paramètres ---

    get cutoff() { return this.params.cutoff; }
    get resonance() { return this.params.resonance; }
    get envMod() { return this.params.envMod; }
    get decay() { return this.params.decay; }
    get accentLevel() { return this.params.accentLevel; }
    get slideTime() { return this.params.slideTime; }
    get waveform() { return this.params.waveform; }
    get distortionAmount() { return this.params.distortion; }
    get drive() { return this.params.drive; }

    // --- Power ON/OFF ---

    /**
     * Allume ou éteint l'instrument pour économiser les ressources
     */
    setPower(on) {
        if (on === this.isPowered) return;

        this.isPowered = on;

        if (on) {
            // Reconnecter au graphe audio
            this.distortion.connect(this.mixerChannel);
        } else {
            // Déconnecter du graphe audio (économie CPU)
            this.distortion.disconnect();
            this.resetSlide();
        }
    }

    getPower() {
        return this.isPowered;
    }
}
